import React, {useEffect, useState} from 'react';
import {PlaidLinkOptions, usePlaidLink, PlaidLinkOnSuccessMetadata, PlaidLinkError} from 'react-plaid-link';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Container, Input, Text} from '../components/@FreeModel';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import {
  gql,
  Error,
  plaidLinkTokenCreate,
  updateApplication,
} from "../db/model";
import {useDispatch} from "react-redux";
import Header from "../components/general/Header/Header";
import {capitalize, InputLabel, MenuItem, Select} from "@mui/material";
import {PlaidLinkOnExitMetadata} from "react-plaid-link/src/types";
import {Mixpanel} from "../observability/Mixpanel";

const BankingInfo = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {applicationID} = useParams();
  const [token, setToken] = useState<string | null>(null)
  const [publicToken, setPublicToken] = useState<string | null>(null)
  const [accountHolderName, setAccountHolderName] = useState("")
  const [accountClass, setAccountClass] = useState("")
  const [institutionName, setInstitutionName] = useState("")
  const [accountSubType, setAccountSubType] = useState("")
  const [accountLastFour, setAccountLastFour] = useState("")
  const isValid = accountHolderName !== '' && accountClass !== ""

  const accountClasses = [{label: "Business", value: "Business"}, {label: "Personal", value: "Personal"}];

  const onSuccess = async(publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    if (metadata === null ||
        metadata.accounts.length === 0 ||
        metadata.institution === null ||
        metadata.institution.name === "") {
      const err: Error = {
        message: "unable to access plaid bank account data",
        path: ["PlaidLink"]
      };
      dispatch({type: 'UPSERT_STATE', state: {errors: [err]}})
      return
    }
    setPublicToken(publicToken)
    setInstitutionName(metadata.institution.name)
    const account = metadata.accounts[0]
    setAccountSubType(capitalize(account.subtype))
    setAccountLastFour(account.mask)
  }

  // onExit is only called when the user specifically marks the X button on plaid
  // Will not be called on a success
  const onExit = async(error:  null | PlaidLinkError, metadata: PlaidLinkOnExitMetadata) => {
    navigate(`/contractors/${applicationID}/steps`)
  }


  const config: PlaidLinkOptions = {
    token,
    onSuccess,
    onExit,
  };
  const {open, ready} = usePlaidLink(config);

  useEffect(() => {
    if (applicationID && !token) {
      // @ts-ignore
      dispatch(gql({
        query: plaidLinkTokenCreate,
        variables: {applicationID: applicationID},
        onSuccess: async (data) => {
          const value = data.createLinkToken?.token ?? ""
          setToken(value)
          Mixpanel.track("contractor-plaid-flow-initiated", {
            "invitation": applicationID,
          })
        }
      }))
    }

    if (ready) {
      open()
    }

  }, [dispatch, applicationID, open, ready, token])

  const addBankingInfo = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // @ts-ignore
    dispatch(gql({
      query: updateApplication,
      variables: {
        id: applicationID,
        form: {
          plaid: {
            accountClass: accountClass,
            nameOnAccount: accountHolderName,
            publicToken: publicToken,
          }
        }
      },
      onSuccess: (_) => {
        Mixpanel.track("contractor-bank-information-added", {
          "invitation": applicationID,
        })
        navigate(`/contractors/${applicationID}/steps`)
      }
    }))
  }

  if (!publicToken) {
    return <div></div>
  }

  return (
      <Container className="bankingInfo">
        <Header title="Add Banking Info"/>
        <Text color="gray">Review the information below and add the name on the bank account.</Text>
        <br/>

        <div className="bank-info">
          <AccountBalanceOutlinedIcon fontSize="large"></AccountBalanceOutlinedIcon>
          <div>
            <Text><strong>{institutionName} {accountSubType}</strong></Text>
            <Text>Account number ending <strong>{accountLastFour}</strong></Text>
          </div>


        </div>
        <form onSubmit={addBankingInfo}>
          <br/>
          <Input
              label="Name on the account"
              id="holder-name"
              type="text"
              value={accountHolderName}
              onChange={e => setAccountHolderName(e.target.value)}
              required
          />
          <br/>
          <br/>
          <Text><strong>IMPORTANT:</strong> Be sure to add the person's name or the business name associated with the bank account.</Text>
          <Text>Examples: John Smith or Smith Family Plumbing</Text>

          <InputLabel
              id="account-class-label"
              size="small"
              variant="standard"
          >Account Type</InputLabel>
          <Select
              className="account-class"
              variant="standard"
              label="Account Type"
              labelId="account-class-label"
              value={accountClass}
              onChange={(e) => setAccountClass(e.target.value)}
              required
          >
            {accountClasses?.map(option => {
              return (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              )
            })}
          </Select>

          <br/>
          <Button disabled={!isValid}>Continue</Button>
        </form>
      </Container>
  );
};

export default BankingInfo;
